import type {
  AlignedPlacement,
  FloatingContext,
  FloatingNodeType,
  ReferenceType,
} from '@floating-ui/react';
import { useFloatingTree } from '@floating-ui/react';
import { usePrevious, useValueRef } from '@timelyapp/react';
import { EMPTY_OBJECT } from 'constants/defaults';
import type { CSSProperties, ForwardedRef } from 'react';
import { useEffect, useMemo } from 'react';
import { lruMemoize } from 'reselect';
import { fromPixels } from 'styles';
import styles from './Popover.module.scss';

export function getFloatingChildren(nodes: Array<FloatingNodeType>, id: string | undefined) {
  const allChildren = nodes.filter(node => node.parentId === id && node.context?.open) ?? [];
  let currentChildren = allChildren;

  while (currentChildren.length) {
    currentChildren =
      nodes.filter(node =>
        currentChildren?.some(n => node.parentId === n.id && node.context?.open)
      ) ?? [];

    Array.prototype.push.apply(allChildren, currentChildren);
  }

  return allChildren;
}

/**
 * Adds a listener that dismisses (closes) the floating element on mouse up. This is not handled by
 * the built-in one from `@floating-ui` (`useDismiss`) as it only supports mouse down.
 */
export function useDismissOnOutsidePointerUp<RT extends ReferenceType = ReferenceType>(
  { events, nodeId, onOpenChange, open, refs }: FloatingContext<RT>,
  enabled = true
) {
  const tree = useFloatingTree();
  const onOpenChangeRef = useValueRef(onOpenChange);

  useEffect(() => {
    if (!open || enabled) return;

    function onPointerUp(event: MouseEvent) {
      const targetIsInsideChildren =
        tree &&
        getFloatingChildren(tree.nodesRef.current, nodeId).some(node =>
          node.context?.refs.floating.current?.contains(event.target as Node)
        );

      if (
        refs.floating.current?.contains(event.target as Node) ||
        refs.domReference.current?.contains(event.target as Node) ||
        targetIsInsideChildren
      ) {
        return;
      }

      events.emit('dismiss');
      onOpenChangeRef.current(false);
    }

    document.addEventListener('pointerup', onPointerUp);
    return () => document.removeEventListener('pointerup', onPointerUp);
  }, [enabled, events, nodeId, onOpenChangeRef, open, refs.domReference, refs.floating, tree]);
}

/**
 * Merges the refs generated by `@floating-ui` with any existing refs for the children. This needs
 * to be a stable function reference when presented with the same refs, otherwise React will get
 * thrown into an infinite render loop.
 */
export const mergeRefs = lruMemoize(
  <T = ReferenceType>(...refs: Array<ForwardedRef<T> | undefined>) =>
    (node: T | null) => {
      for (const ref of refs) {
        if (!ref) continue;

        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
      }
    }
);

export const ARROW_SVG_WIDTH = fromPixels(styles.popoverArrowWidth);
export const ARROW_SVG_HEIGHT = fromPixels(styles.popoverArrowHeight);

/**
 * Calculates the arrow style with static placement on both axes. Normally it would only respect
 * `top` or `left` for example, and then it would be centered on the cross axis. Using this hook
 * we can position the arrow correctly for alignments like `bottom-start`.
 */
export function useArrowStyle<RT extends ReferenceType = ReferenceType>(
  context: FloatingContext<RT>
): CSSProperties {
  const reference = context.refs.reference?.current as HTMLElement | null;
  const floating = context.refs.floating?.current as HTMLElement | null;
  const { x, y } = context.middlewareData.arrow ?? {};
  const { placement } = context;

  const currentStyle = useMemo(() => {
    if (!reference || !floating || !placement) return EMPTY_OBJECT;

    const [side, alignment] = placement.split('-');

    const unsetSides = {
      top: '',
      bottom: '',
      left: '',
      right: '',
    };

    const mainSide = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
    }[side]!;

    const rotation = {
      top: '180deg',
      bottom: '0deg',
      left: '90deg',
      right: '-90deg',
    }[side]!;

    const style: CSSProperties = {
      transform: `rotate(${rotation})`,
    };

    const isRefWider = reference.offsetWidth > floating.offsetWidth;
    const isRefTaller = reference.offsetHeight > floating.offsetHeight;
    const isVerticalSide = side === 'top' || side === 'bottom';
    const useStaticPositioning = !!alignment && (isVerticalSide ? isRefWider : isRefTaller);

    // TODO: This is a hand tweaked number to make it clip the border as nicely as possible, but
    // should be calculated automatically to make it perfect
    const adjustment = isVerticalSide ? 2 : 3;

    const arrowOffset = `-${ARROW_SVG_WIDTH - ARROW_SVG_HEIGHT / 2 - adjustment}px`;

    if (useStaticPositioning) {
      const crossSide = {
        'top-start': 'left',
        'top-end': 'right',
        'bottom-start': 'left',
        'bottom-end': 'right',
        'left-start': 'top',
        'left-end': 'bottom',
        'right-start': 'top',
        'right-end': 'bottom',
      }[placement as AlignedPlacement];

      Object.assign(style, {
        ...unsetSides,
        [mainSide]: arrowOffset,
        [crossSide]: 'min(15%, 15px)',
      });
    } else {
      Object.assign(style, {
        ...unsetSides,
        left: x != null ? `${x}px` : '',
        top: y != null ? `${y}px` : '',
        [mainSide]: arrowOffset,
      });
    }

    return style;
  }, [reference, floating, placement, x, y]);

  const previousStyle = usePrevious(currentStyle);
  return !Object.keys(currentStyle).length ? previousStyle : currentStyle;
}
